import { Component, OnInit, OnDestroy, SecurityContext } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalConfig, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { QaService } from '../../../services/qa/qa.service';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { Status } from 'src/app/data-models/widgets/status.model';
import { CommonService } from 'src/app/services/common/common.service';
import { DeviceService } from 'src/app/services/device/device.service';
import { AppNotification } from 'src/app/data-models/common/app-notification.model';
import { NetworkStatus } from 'src/app/enums/network-status.enum';
import { SystemUpdateService } from 'src/app/services/system-update/system-update.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { SessionStorageKey } from 'src/app/enums/session-storage-key-enum';
import { HypothesisService } from 'src/app/services/hypothesis/hypothesis.service';
import { AdPolicyService } from 'src/app/services/ad-policy/ad-policy.service';
import { LocalStorageKey } from 'src/app/enums/local-storage-key.enum';
import { VantageShellService } from 'src/app/services/vantage-shell/vantage-shell.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { WarrantyService } from 'src/app/services/warranty/warranty.service';
import { FeatureContent } from 'src/app/data-models/common/feature-content.model';
import { Subscription } from 'rxjs/internal/Subscription';
import { FormatLocaleDatePipe } from 'src/app/pipe/format-locale-date/format-locale-date.pipe';
import { MemoryDiskUsage } from 'src/app/data-models/widgets/memory-disk-usage.model';
import { FeatureToggleService } from 'src/app/services/feature-toggle/feature-toggle.service';
import { WarrantyInfo } from 'src/app/data-models/widgets/warranty-info.model';
import { DashboardLocalStorageKey } from 'src/app/enums/dashboard-local-storage-key.enum';
import { SystemUpdatePresenterService } from 'src/app/presenters/user-cases/system-update/system-update-presenter';

@Component({
	selector: 'vtr-page-dashboard',
	templateUrl: './page-dashboard.component.html',
	styleUrls: ['./page-dashboard.component.scss'],
})
export class PageDashboardComponent implements OnInit, OnDestroy {
	offlineConnection = 'offline-connection';
	public systemStatus: Status[] = [];
	public memoryDiskUsage: MemoryDiskUsage = new MemoryDiskUsage();
	public isOnline = true;
	public brand;
	private protocolAction: any;
	private lastAction: any;
	public warrantyData: WarrantyInfo = {
		info: { endDate: null, status: 2, startDate: null, url: '' },
		cache: true,
	};
	public hasQuickSettings = true;
	public isQuickSettingsHidden = true;
	public hardwareSettingsAvailable = true;
	public isGiveFeedbackVisible = true;

	private subscription: Subscription;

	supportDatas = {
		documentation: [
			{
				icon: ['fal', 'book'],
				title: 'support.documentation.listUserGuide',
				clickItem: 'userGuide',
			},
		],
	};

	contentCards = {
		positionA: {
			displayContent: [],
			template: 'home-page-hero-banner',
			cardId: 'positionA',
			positionParam: 'position-A',
			tileSource: 'CMS',
			cmsContent: undefined,
			upeContent: undefined,
		},
		positionB: {
			displayContent: new FeatureContent(),
			template: 'half-width-title-description-link-image',
			cardId: 'positionB',
			positionParam: 'position-B',
			tileSource: 'CMS',
			cmsContent: undefined,
			upeContent: undefined,
		},
		positionC: {
			displayContent: new FeatureContent(),
			template: 'half-width-title-description-link-image',
			cardId: 'positionC',
			positionParam: 'position-C',
			tileSource: 'CMS',
			cmsContent: undefined,
			upeContent: undefined,
		},
		positionD: {
			displayContent: new FeatureContent(),
			template: 'full-width-title-image-background',
			cardId: 'positionD',
			positionParam: 'position-D',
			tileSource: 'CMS',
			cmsContent: undefined,
			upeContent: undefined,
		},
		positionE: {
			displayContent: new FeatureContent(),
			template: 'half-width-top-image-title-link',
			cardId: 'positionE',
			positionParam: 'position-E',
			tileSource: 'CMS',
			cmsContent: undefined,
			upeContent: undefined,
		},
		positionF: {
			displayContent: new FeatureContent(),
			template: 'half-width-top-image-title-link',
			cardId: 'positionF',
			positionParam: 'position-F',
			tileSource: 'CMS',
			cmsContent: undefined,
			upeContent: undefined,
		},
	};

	constructor(
		private router: Router,
		public dashboardService: DashboardService,
		public qaService: QaService,
		private config: NgbModalConfig,
		public commonService: CommonService,
		public formatLocaleDate: FormatLocaleDatePipe,
		public deviceService: DeviceService,
		private systemUpdateService: SystemUpdateService,
		private translate: TranslateService,
		private vantageShellService: VantageShellService,
		private activatedRoute: ActivatedRoute,
		private dialogService: DialogService,
		private logger: LoggerService,
		private hypService: HypothesisService,
		public warrantyService: WarrantyService,
		private adPolicyService: AdPolicyService,
		private featureToggleService: FeatureToggleService,
		private systemUpdatePresenter: SystemUpdatePresenterService
	) {}

	ngOnInit() {
		this.getProtocalAction();
		this.config.backdrop = 'static';
		this.config.keyboard = false;
		this.isOnline = this.commonService.isOnline;
		this.deviceService.getMachineInfo().then(() => {
			this.setDefaultSystemStatus();
			if (this.dashboardService.isShellAvailable) {
				this.logger.info('PageDashboardComponent.getSystemInfo');
				this.getSystemInfo();
			}
		});
		this.brand = this.commonService.getLocalStorageValue(LocalStorageKey.MachineType, -1);
		this.qaService.getQATranslation(this.translate); // VAN-5872, server switch feature

		this.isQuickSettingsHidden = this.commonService.getLocalStorageValue(
			DashboardLocalStorageKey.IsQuickSettingsHidden,
			true
		);
		this.dashboardService.isDashboardDisplayed = true;
		this.commonService.setSessionStorageValue(SessionStorageKey.DashboardInDashboardPage, true);
		this.subscription = this.commonService.notification.subscribe(
			(notification: AppNotification) => {
				this.onNotification(notification);
			}
		);

		this.translate
			.stream([
				'dashboard.offlineInfo.welcomeToVantage',
				'common.menu.support',
				'common.menu.device.sub2',
				'dashboard.offlineInfo.systemHealth',
				'settings.preferenceSettings',
				'systemUpdates.title',
				'systemUpdates.readMore',
			])
			.subscribe((result) => {
				this.dashboardService.translateString = result;
				this.dashboardService.setDefaultCMSContent();
				this.getOfflineContent();
			});
		this.getSelfSelectStatus();
		this.launchProtocol();
	}

	private getProtocalAction() {
		this.protocolAction = this.activatedRoute.snapshot.queryParams.action;
		const currentNavigate = this.router.getCurrentNavigation();
		if (currentNavigate && this.router.getCurrentNavigation().extras !== undefined) {
			const extras = this.router.getCurrentNavigation().extras;
			if (
				!this.protocolAction &&
				extras.queryParams !== undefined &&
				extras.queryParams.action !== undefined
			) {
				this.protocolAction = extras.queryParams.action;
			}
		}
	}

	private launchProtocol() {
		if (this.protocolAction && this.lastAction !== this.protocolAction) {
			this.lastAction = this.protocolAction;
		}
	}

	ngOnDestroy() {
		this.dashboardService.isDashboardDisplayed = false;
		this.commonService.setSessionStorageValue(
			SessionStorageKey.DashboardInDashboardPage,
			false
		);
		this.qaService.destroyChangeSubscribed();
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	private getOfflineContent() {
		Object.keys(this.contentCards).forEach((cardId) => {
			this.contentCards[cardId].displayContent =
				this.dashboardService.offlineCardContent[cardId];
		});
	}

	private setDefaultSystemStatus() {
		const memory = new Status();
		memory.status = 4;
		memory.id = 'memory';
		memory.metricsItemName = 'Memory';

		this.translate.stream('dashboard.systemStatus.memory.title').subscribe((value) => {
			memory.title = value;
		});

		this.translate
			.stream('dashboard.systemStatus.memory.detail.notFound')
			.subscribe((value) => {
				memory.detail = value;
			});

		memory.path = 'ms-settings:about';
		memory.asLink = false;
		memory.isSystemLink = true;
		memory.type = 'system';
		this.systemStatus[0] = memory;

		const disk = new Status();
		disk.status = 4;
		disk.id = 'disk';
		disk.metricsItemName = 'Disk Space';

		this.translate.stream('dashboard.systemStatus.diskSpace.title').subscribe((value) => {
			disk.title = value;
		});

		this.translate
			.stream('dashboard.systemStatus.diskSpace.detail.notFound')
			.subscribe((value) => {
				disk.detail = value;
			});

		disk.path = 'ms-settings:storagesense';
		disk.asLink = false;
		disk.isSystemLink = true;
		disk.type = 'system';
		this.systemStatus[1] = disk;

		const warranty = new Status();
		warranty.status = 4;
		warranty.id = 'warranty';
		warranty.metricsItemName = 'Warranty';

		this.translate.stream('dashboard.systemStatus.warranty.title').subscribe((value) => {
			warranty.title = value;
		});

		this.translate
			.stream('dashboard.systemStatus.warranty.detail.notFound')
			.subscribe((value) => {
				warranty.detail = value;
			});

		warranty.path = '/support';
		warranty.asLink = false;
		/* warranty.isSystemLink = true; */
		warranty.isSystemLink = false;
		warranty.type = 'system';
		this.systemStatus[2] = warranty;

		if (
			this.deviceService &&
			this.adPolicyService &&
			this.adPolicyService.IsSystemUpdateEnabled
		) {
			const systemUpdate = new Status();
			systemUpdate.status = 4;
			systemUpdate.id = 'systemupdate';
			systemUpdate.metricsItemName = 'System Update';

			this.translate
				.stream('dashboard.systemStatus.systemUpdate.title')
				.subscribe((value) => {
					systemUpdate.title = value;
				});

			this.translate
				.stream('dashboard.systemStatus.systemUpdate.detail.update')
				.subscribe((value) => {
					systemUpdate.detail = value;
				});

			systemUpdate.path = 'device/system-updates';
			systemUpdate.asLink = true;
			systemUpdate.isSystemLink = false;
			systemUpdate.type = 'system';
			this.systemStatus[3] = systemUpdate;
		}
	}

	private getSystemInfo() {
		// ram and disk
		this.memoryDiskUsage = this.commonService.getLocalStorageValue(
			DashboardLocalStorageKey.MemoryDiskUsage,
			new MemoryDiskUsage()
		);
		this.dashboardService.getMemoryDiskUsage().then((value) => {
			if (value) {
				const memory = this.systemStatus[0];
				const totalRam = value.memory.total;
				const usedRam = value.memory.used;
				const freeRam = value.memory.total - value.memory.used;
				const usedRamFormated = this.commonService.formatBytes(usedRam, 1);
				const totalRamFormated = this.commonService.formatBytes(totalRam, 1);
				const freeRamFormated = this.commonService.formatBytes(freeRam, 1);
				if (usedRam === totalRam) {
					memory.status = 1;
				} else {
					memory.status = 0;
				}
				const disk = this.systemStatus[1];
				const totalDisk = value.disk.total;
				const usedDisk = value.disk.used;
				const availableDisk = value.disk.total - value.disk.used;
				const usedDiskFormated = this.commonService.formatBytes(usedDisk, 1);
				const totalDiskFormated = this.commonService.formatBytes(totalDisk, 1);
				const availableDiskFormated = this.commonService.formatBytes(availableDisk, 1);
				this.translate.stream('dashboard.systemStatus.memory.detail.of').subscribe((re) => {
					memory.detail = `${usedRamFormated} ${re} ${totalRamFormated}`;
					disk.detail = `${usedDiskFormated} ${re} ${totalDiskFormated}`;
				});
				if (usedDisk === totalDisk) {
					disk.status = 1;
				} else {
					disk.status = 0;
				}
				// update memory disk usage for Storage widget
				this.memoryDiskUsage.memory = {
					used: usedRam,
					usedText: usedRamFormated,
					total: totalRam,
					totalText: totalRamFormated,
					free: freeRam,
					freeText: freeRamFormated,
				};
				this.memoryDiskUsage.disk = {
					used: usedDisk,
					usedText: usedDiskFormated,
					total: totalDisk,
					totalText: totalDiskFormated,
					free: availableDisk,
					freeText: availableDiskFormated,
				};
				this.commonService.setLocalStorageValue(
					DashboardLocalStorageKey.MemoryDiskUsage,
					this.memoryDiskUsage
				);
			}
		});

		// warranty
		this.getWarrantyInfo();

		// system update
		if (
			this.deviceService &&
			this.adPolicyService &&
			this.adPolicyService.IsSystemUpdateEnabled
		) {
			this.dashboardService.getRecentUpdateInfo().subscribe((value) => {
				if (value) {
					const systemUpdate = this.systemStatus[3];
					const diffInDays = this.systemUpdateService.dateDiffInDays(value.lastupdate);
					if (value.status === 1) {
						if (diffInDays > 30) {
							systemUpdate.status = 1;
						} else {
							systemUpdate.status = 0;
						}
					} else {
						systemUpdate.status = 1;
					}
					systemUpdate.status = 0;
				}
			});
		}
	}

	getWarrantyInfo() {
		this.warrantyService.getWarrantyInfo().subscribe((value) => {
			if (value) {
				this.setWarrantyInfo(value);
			}
		});
	}

	setWarrantyInfo(value: any) {
		this.warrantyData = {
			info: {
				startDate: value.startDate,
				endDate: value.endDate,
				status: value.status,
				url: this.warrantyService.getWarrantyUrl(),
			},
			cache: true,
		};
		const warranty = this.systemStatus[2];
		const warrantyDate = this.formatLocaleDate.transform(value.endDate);
		// in warranty
		if (value.status === 0) {
			this.translate
				.stream('dashboard.systemStatus.warranty.detail.until')
				.subscribe((re) => {
					warranty.detail = `${re} ${warrantyDate}`; // `Until ${warrantyDate}`;
				});
			warranty.status = 0;
		} else if (value.status === 1) {
			this.translate
				.stream('dashboard.systemStatus.warranty.detail.expiredOn')
				.subscribe((re) => {
					warranty.detail = `${re} ${warrantyDate}`; // `Warranty expired on ${warrantyDate}`;
				});
			warranty.status = 1;
		} else {
			this.translate
				.stream('dashboard.systemStatus.warranty.detail.notAvailable')
				.subscribe((re) => {
					warranty.detail = `${re}`; //  'Warranty not available';
				});
			warranty.status = 1;
		}
		warranty.isHidden = !this.deviceService.showWarranty;
		warranty.status = 0;
	}

	private onNotification(notification: AppNotification) {
		if (notification) {
			switch (notification.type) {
				case NetworkStatus.Online:
				case NetworkStatus.Offline:
					this.isOnline = notification.payload.isOnline;
					if (this.isOnline) {
						this.getWarrantyInfo();
					}
					break;
				case LocalStorageKey.LastWarrantyStatus:
					if (notification.payload) {
						this.setWarrantyInfo(notification.payload);
					}
					break;
				default:
					break;
			}
		}
	}
	// checking self select status for HW Settings
	private getSelfSelectStatus() {
		this.dashboardService.getSelfSelectStatus().then((hardwareSettingsAvailable) => {
			this.hardwareSettingsAvailable = hardwareSettingsAvailable;
		});
	}

	public setQuickSettingsAvailability(device: any) {
		const isQuickSettingsHidden = !device.microphoneAvailable;
		this.hasQuickSettings = !isQuickSettingsHidden;
		this.isQuickSettingsHidden = isQuickSettingsHidden;
		this.commonService.setLocalStorageValue(
			DashboardLocalStorageKey.IsQuickSettingsHidden,
			this.isQuickSettingsHidden
		);
	}

	public isShowSystemUpdateWidget(): boolean {
		return this.systemUpdatePresenter.isVisible();
	}

	public onFeedbackVisibiltyChange(isVisible: boolean) {
		this.isGiveFeedbackVisible = isVisible;
	}
}
